import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadLabelBatch} from '../../../models/zad'
import {ZadApiError} from '../zad/errors'
import {
  cancelZadLabelBatch,
  createZadLabelBatch,
  fetchZadLabelBatchById,
  fetchZadLabelBatches,
  markZadLabelBatchReady,
  recordZadLabelBatchPrint,
  refreshZadLabelBatchPrices,
  updateZadLabelBatch,
} from './service'
import {FetchZadLabelBatchesResponse, ZadLabelBatchState} from './types'

export const zadLabelBatchInitialState: ZadLabelBatchState = {
  labelBatches: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  listInitialized: false,
  actionLoading: false,
  actionKind: null,
  selectedBatch: null,
  error: null,
  errorCode: null,
  errorStatus: null,
}

function applyRejected(state: ZadLabelBatchState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
  state.actionLoading = false
  state.actionKind = null
}

function clearError(state: ZadLabelBatchState) {
  state.error = null
  state.errorCode = null
  state.errorStatus = null
}

function upsertBatch(state: ZadLabelBatchState, batch?: ZadLabelBatch | null) {
  if (!batch?._id) return
  state.selectedBatch = batch
  const index = state.labelBatches.findIndex((item) => item._id === batch._id)
  if (index >= 0) state.labelBatches[index] = batch
  else state.labelBatches = [batch, ...state.labelBatches]
}

const zadLabelBatchSlice = createSlice({
  name: 'zadLabelBatches',
  initialState: zadLabelBatchInitialState,
  reducers: {
    clearZadLabelBatchError: (state: ZadLabelBatchState) => {
      clearError(state)
    },
    clearSelectedZadLabelBatch: (state: ZadLabelBatchState) => {
      state.selectedBatch = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZadLabelBatches.pending, (state) => {
      state.listLoading = true
      clearError(state)
    })
    builder.addCase(fetchZadLabelBatches.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.listLoading = false
      state.listInitialized = true
    })
    builder.addCase(
      fetchZadLabelBatches.fulfilled,
      (state, action: PayloadAction<FetchZadLabelBatchesResponse>) => {
        state.listLoading = false
        state.listInitialized = true
        clearError(state)
        state.labelBatches = action.payload.labelBatches
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      }
    )

    builder.addCase(fetchZadLabelBatchById.pending, (state) => {
      state.actionLoading = true
      state.actionKind = null
    })
    builder.addCase(fetchZadLabelBatchById.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
    })
    builder.addCase(fetchZadLabelBatchById.fulfilled, (state, action) => {
      state.actionLoading = false
      upsertBatch(state, action.payload.labelBatch)
    })

    const pendingKind =
      (kind: ZadLabelBatchState['actionKind']) => (state: ZadLabelBatchState) => {
        state.actionLoading = true
        state.actionKind = kind
        clearError(state)
      }

    builder.addCase(createZadLabelBatch.pending, pendingKind('save'))
    builder.addCase(createZadLabelBatch.rejected, applyRejected)
    builder.addCase(createZadLabelBatch.fulfilled, (state, action) => {
      state.actionLoading = false
      state.actionKind = null
      clearError(state)
      upsertBatch(state, action.payload.labelBatch)
    })

    builder.addCase(updateZadLabelBatch.pending, pendingKind('save'))
    builder.addCase(updateZadLabelBatch.rejected, applyRejected)
    builder.addCase(updateZadLabelBatch.fulfilled, (state, action) => {
      state.actionLoading = false
      state.actionKind = null
      clearError(state)
      upsertBatch(state, action.payload.labelBatch)
    })

    builder.addCase(markZadLabelBatchReady.pending, pendingKind('ready'))
    builder.addCase(markZadLabelBatchReady.rejected, applyRejected)
    builder.addCase(markZadLabelBatchReady.fulfilled, (state, action) => {
      state.actionLoading = false
      state.actionKind = null
      clearError(state)
      upsertBatch(state, action.payload.labelBatch)
    })

    builder.addCase(cancelZadLabelBatch.pending, pendingKind('cancel'))
    builder.addCase(cancelZadLabelBatch.rejected, applyRejected)
    builder.addCase(cancelZadLabelBatch.fulfilled, (state, action) => {
      state.actionLoading = false
      state.actionKind = null
      clearError(state)
      upsertBatch(state, action.payload.labelBatch)
    })

    builder.addCase(recordZadLabelBatchPrint.pending, pendingKind('print'))
    builder.addCase(recordZadLabelBatchPrint.rejected, applyRejected)
    builder.addCase(recordZadLabelBatchPrint.fulfilled, (state, action) => {
      state.actionLoading = false
      state.actionKind = null
      clearError(state)
      upsertBatch(state, action.payload.labelBatch)
    })

    builder.addCase(refreshZadLabelBatchPrices.pending, pendingKind('refresh'))
    builder.addCase(refreshZadLabelBatchPrices.rejected, applyRejected)
    builder.addCase(refreshZadLabelBatchPrices.fulfilled, (state, action) => {
      state.actionLoading = false
      state.actionKind = null
      clearError(state)
      upsertBatch(state, action.payload.labelBatch)
    })
  },
})

export const {clearZadLabelBatchError, clearSelectedZadLabelBatch} = zadLabelBatchSlice.actions
export const actions = zadLabelBatchSlice.actions
export default zadLabelBatchSlice.reducer
